import { useTheme } from "@emotion/react";
import * as Popover from "@radix-ui/react-popover";
import React, { useRef, useState } from "react";
import { MdKeyboardArrowDown } from "react-icons/md";

type Option = {
  value: string;
  label: string;
};

export type SelectDropdownProps = {
  options: Option[];
  onChange: (selectedValue: string) => void;
  value?: string | null;
  className?: string;
  placeholder?: string;
  isDisabled?: boolean;
  allowEmptySelection?: boolean;
};

export const SelectDropdown: React.FC<SelectDropdownProps> = (props) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [panelWidth, setPanelWidth] = useState<number | undefined>();

  const selectedOption = props.value ? props.options.find((o) => o.value === props.value) : undefined;
  const displayLabel = selectedOption?.label ?? props.placeholder ?? "Wybierz ...";

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next && triggerRef.current) {
      setPanelWidth(triggerRef.current.offsetWidth);
    }
  };

  const pick = (nextValue: string) => {
    props.onChange(nextValue);
    setOpen(false);
  };

  const rowHover = {
    background: theme.colors.hover,
  };

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          ref={triggerRef}
          disabled={props.isDisabled}
          className={props.className}
          css={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "0.5rem",
            padding: "0.4375rem 0.5rem",
            border: `1px solid ${theme.colors.border}`,
            borderRadius: theme.smallBorderRadius,
            backgroundColor: theme.colors.background,
            fontSize: "1rem",
            color: selectedOption ? theme.colors.primary : "#757575",
            cursor: props.isDisabled ? "not-allowed" : "pointer",
            opacity: props.isDisabled ? 0.6 : 1,
            ":focus-visible": {
              outlineStyle: "solid",
              outlineColor: theme.colors.primary,
              outlineWidth: "1px",
            },
          }}
        >
          <span
            css={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              textAlign: "left",
              flex: 1,
              minWidth: 0,
            }}
          >
            {displayLabel}
          </span>
          <MdKeyboardArrowDown size={20} color={theme.colors.secondaryText} />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={4}
          collisionPadding={8}
          css={{
            zIndex: 10000,
            borderRadius: theme.smallBorderRadius,
            backgroundColor: theme.colors.background,
            border: `1px solid ${theme.colors.border}`,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
            padding: 0,
            minWidth: panelWidth ? `${Math.max(panelWidth, 120)}px` : undefined,
          }}
        >
          <div
            css={{
              maxHeight: "min(50vh, 280px)",
              overflowY: "auto",
              padding: "0.25rem",
            }}
          >
            {props.allowEmptySelection && (
              <button
                type="button"
                onClick={() => pick("")}
                css={{
                  width: "100%",
                  display: "block",
                  textAlign: "left",
                  padding: "0.5rem 0.75rem",
                  border: "none",
                  borderRadius: theme.smallBorderRadius,
                  background: props.value === "" ? theme.colors.primaryLight : "transparent",
                  color: theme.colors.primary,
                  fontSize: "0.9375rem",
                  fontWeight: props.value === "" ? 700 : 400,
                  cursor: "pointer",
                  ":hover": rowHover,
                }}
              >
                {props.placeholder ?? "Wybierz ..."}
              </button>
            )}
            {props.options.map((option) => {
              const isSelected = props.value === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => pick(option.value)}
                  css={{
                    width: "100%",
                    display: "block",
                    textAlign: "left",
                    padding: "0.5rem 0.75rem",
                    border: "none",
                    borderRadius: theme.smallBorderRadius,
                    background: isSelected ? theme.colors.primaryLight : "transparent",
                    color: theme.colors.primary,
                    fontSize: "0.9375rem",
                    fontWeight: isSelected ? 700 : 400,
                    cursor: "pointer",
                    ":hover": rowHover,
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
